/**
 * Drawing primitives layered on top of the pitch — §7 of the tactics
 * board spec. The base `TacticsDrawing` slot on `TacticsLayout` is
 * intentionally permissive (`Record<string, unknown> & { id: string }`
 * — see `./types.ts`) so this module owns the concrete shape.
 *
 * A drawing represents one of eight kinds: line, arrow, curved-arrow,
 * rect, ellipse, text, freehand, measure. The toolbar (apps/web2
 * `DrawingToolbar`) edits the active tool / colour / stroke / dash;
 * the on-pitch capture layer (mounted by the boards next round) emits
 * the actual `points: {x, z}[]` in world metres.
 *
 * The `measure` kind is the Piero / Sky-telestrator "distance between
 * players" gesture — a two-point segment that prints its own world-
 * metre length alongside the line. The renderer is region-aware
 * (metres vs yards); the stored value is always two world-metre
 * `{x, z}` points.
 *
 * Coordinate system mirrors the rest of the tactics package: world
 * metres, pitch-centred origin. The 2D renderer applies the
 * `SVG-x = -world.z, SVG-y = world.x` axis swap when drawing — see
 * `apps/web2/src/components/tactics2d/DrawingLayer2D.tsx`.
 */

// ---------------------------------------------------------------------------
// String-union enums — exported as const tuples so callers can also
// iterate them at runtime (e.g. the toolbar mapping over kinds).
// ---------------------------------------------------------------------------

export const DRAWING_KINDS = [
  'line',
  'arrow',
  'curved-arrow',
  'rect',
  'ellipse',
  'text',
  'freehand',
  'measure',
  // Manual spacing zone — N-point polygon authored corner-by-corner.
  // Lives alongside the analytical Spacing overlay (voronoi); the zone
  // tool lets the coach paint a custom region (e.g. "press trap",
  // "midfield channel") on top of the auto-computed territory.
  'zone',
] as const;
export type DrawingKind = (typeof DRAWING_KINDS)[number];

export const DASH_STYLES = ['solid', 'dashed', 'dotted'] as const;
export type DashStyle = (typeof DASH_STYLES)[number];

export const ARROW_HEADS = ['none', 'standard', 'open', 'curved'] as const;
export type ArrowHead = (typeof ARROW_HEADS)[number];

// ---------------------------------------------------------------------------
// Drawing shape
// ---------------------------------------------------------------------------

/** One drawing primitive layered on top of the pitch. Points are in
 *  world metres. Stroke / fill are CSS hex colours.
 *
 *  Kind-specific point semantics:
 *  - line / arrow / curved-arrow — two points (start, end). curved-arrow
 *    treats the implicit midpoint as the bezier control handle.
 *  - rect — two points (corner, opposite corner).
 *  - ellipse — two points (corner, opposite corner) bounding the box.
 *  - text — single anchor point (`points[0]`).
 *  - freehand — sampled polyline; ≥ 2 points.
 *  - measure — two points (start, end). Renderer prints a region-aware
 *    distance label near the midpoint (metres for UK, yards for US).
 */
export interface TacticsDrawing {
  id: string;
  kind: DrawingKind;
  points: { x: number; z: number }[];
  /** CSS hex (e.g. '#86efac'). Stroke colour for line/arrow/text/freehand
   *  shapes and the outline of rect/ellipse. */
  stroke: string;
  /** Optional fill — only rect / ellipse use this; absent / 'transparent'
   *  renders an outline only. */
  fill?: string;
  /** Stroke width in metres (world units). Clamped to [0.1, 2.0] by
   *  `clampStrokeWidth`. */
  strokeWidth: number;
  /** Dash pattern. Defaults to 'solid' when absent at render time. */
  dash?: DashStyle;
  /** Arrow head shape — only meaningful for arrow / curved-arrow. */
  arrowHead?: ArrowHead;
  /** Text content — only meaningful for kind='text'. */
  text?: string;
}

// ---------------------------------------------------------------------------
// Palette — 8 hex swatches sized for legibility on the dark stadium
// pitch. Amber/yellow first — the broadcast annotation default (Hawk-
// Eye, Sky MNF, NFL telestrator all anchor on yellow over grass);
// then the brand `--green-bright` (zone-default + home-territory
// signature colour), white as a neutral alt highlight, the red/blue
// design-token accents, magenta + cyan for diagrammatic contrast, and
// a neutral mid-grey for subdued annotation strokes.
// ---------------------------------------------------------------------------

export const DEFAULT_PALETTE: readonly string[] = [
  '#fbbf24', // amber/yellow — default (broadcast annotation idiom)
  '#86efac', // green-bright (matches --green-bright)
  '#ffffff', // white — alt highlight
  '#fb7185', // red / rose (matches --rose token)
  '#7dd3fc', // blue (matches --info token)
  '#f0abfc', // magenta — accent contrast
  '#67e8f9', // cyan — secondary highlight
  '#9ca3af', // neutral grey — subdued annotation
] as const;

// ---------------------------------------------------------------------------
// Factory: sensible defaults per kind
// ---------------------------------------------------------------------------

/**
 * Sensible default style bag per drawing kind. Used by the toolbar /
 * capture layer when the coach picks a tool fresh — they get a usable
 * stroke + dash + head out of the box. Returns everything except `id`
 * and `points`, which the call site assigns at creation time.
 */
export function defaultDrawing(
  kind: DrawingKind,
): Omit<TacticsDrawing, 'id' | 'points'> {
  const base = {
    kind,
    stroke: DEFAULT_PALETTE[0],   // amber/yellow — broadcast default
    // Bumped from 0.35 → 0.6 m for broadcast-style chunky strokes —
    // reads correctly on a coach's TV-sized screen viewed at touchline
    // distance, mirrors Sky-MNF / Hawk-Eye line weights.
    strokeWidth: 0.6,
    dash: 'solid' as DashStyle,
  };
  switch (kind) {
    case 'arrow':
      return { ...base, arrowHead: 'standard' };
    case 'curved-arrow':
      return { ...base, arrowHead: 'curved' };
    case 'rect':
    case 'ellipse':
      return { ...base, fill: 'transparent' };
    case 'text':
      // Text labels render thicker so the glyphs read on the dark pitch.
      return { ...base, strokeWidth: 0.75, text: '' };
    case 'measure':
      // Distance-measure lines are slightly thinner than the standard
      // annotation stroke so the printed label stays the focal point.
      return { ...base, strokeWidth: 0.4, dash: 'dashed' as DashStyle };
    case 'zone':
      // Manual spacing zone — semi-transparent green fill so it reads
      // as a region, not a heavy block. Coach can re-tint via the
      // colour palette (fill follows stroke at render time).
      return {
        ...base,
        stroke: DEFAULT_PALETTE[1],   // green-bright
        strokeWidth: 0.25,
        fill: 'auto',                 // sentinel — renderer derives rgba from stroke
      };
    case 'freehand':
    case 'line':
    default:
      return base;
  }
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/**
 * Euclidean distance in world metres between two points on the pitch.
 *
 * Used by the §7 distance-measure drawing tool — the renderer prints a
 * region-aware label near the midpoint, but the underlying length is
 * always stored / computed in metres regardless of region.
 *
 * Non-finite components fall back to `0` for the affected delta so a
 * partially-corrupt input (e.g. a half-captured point) never propagates
 * a `NaN` into the label.
 */
export function measureDistanceMetres(
  a: { x: number; z: number },
  b: { x: number; z: number },
): number {
  const dx = Number.isFinite(a.x) && Number.isFinite(b.x) ? b.x - a.x : 0;
  const dz = Number.isFinite(a.z) && Number.isFinite(b.z) ? b.z - a.z : 0;
  return Math.sqrt(dx * dx + dz * dz);
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/** Clamp a stroke width to the renderable range. Values outside [0.1, 2.0]
 *  metres either disappear against the pitch (too thin) or swamp the
 *  player tokens (too thick). */
export function clampStrokeWidth(value: number): number {
  if (!Number.isFinite(value)) return 0.1;
  if (value < 0.1) return 0.1;
  if (value > 2.0) return 2.0;
  return value;
}

const KIND_SET: ReadonlySet<string> = new Set(DRAWING_KINDS);
const DASH_SET: ReadonlySet<string> = new Set(DASH_STYLES);
const HEAD_SET: ReadonlySet<string> = new Set(ARROW_HEADS);

/** Type guard for one drawing entry. Rejects anything missing the
 *  required scalars, anything whose `points` isn't an array of
 *  `{x, z}` pairs, anything with an unknown `kind`, and any present-
 *  but-invalid optional field (`dash`, `arrowHead`). Used when reading
 *  the `drawings?: TacticsDrawing[]` slot off a stored layout so a
 *  corrupt entry no-ops instead of crashing the renderer. */
export function isTacticsDrawing(value: unknown): value is TacticsDrawing {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;

  if (typeof v.id !== 'string' || v.id.length === 0) return false;
  if (typeof v.kind !== 'string' || !KIND_SET.has(v.kind)) return false;
  if (typeof v.stroke !== 'string' || v.stroke.length === 0) return false;
  if (typeof v.strokeWidth !== 'number' || !Number.isFinite(v.strokeWidth)) {
    return false;
  }
  if (!Array.isArray(v.points)) return false;
  for (const p of v.points) {
    if (!p || typeof p !== 'object') return false;
    const pt = p as Record<string, unknown>;
    if (typeof pt.x !== 'number' || !Number.isFinite(pt.x)) return false;
    if (typeof pt.z !== 'number' || !Number.isFinite(pt.z)) return false;
  }

  // Optional fields — only validate when present.
  if (v.dash !== undefined && (typeof v.dash !== 'string' || !DASH_SET.has(v.dash))) {
    return false;
  }
  if (
    v.arrowHead !== undefined &&
    (typeof v.arrowHead !== 'string' || !HEAD_SET.has(v.arrowHead))
  ) {
    return false;
  }
  if (v.fill !== undefined && typeof v.fill !== 'string') return false;
  if (v.text !== undefined && typeof v.text !== 'string') return false;

  return true;
}
